import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { OnboardingState, OnboardingStateResult } from './useGetOnboardingState';
import { sessionStore } from 'src/store/sessionStore';

interface SaveOnboardingPayload {
  uid: string;
  step: number | string;
  data:
    | string
    | string[]
    | {
        timezone?: string;
        weekly_schedule?: unknown;
        days?: number;
        skipped?: boolean;
        onboardingSkipped?: boolean;
        [key: string]: unknown;
      }
    | boolean; // slug for step 1, array of slugs/strings for 2 & 3, schedule for step 4, or skip
}

export function useSaveOnboardingStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveOnboardingPayload) => {
      const { uid, step, data } = payload;

      if (!uid) {
        throw new Error('User UID is required to save onboarding progress');
      }

      const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_SAVE_ONBOARDING_STEP}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid, step, data }),
      });

      if (!response.ok) {
        throw new Error('Failed to save onboarding progress');
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      const queryKey = ['onboarding-state', variables.uid];

      // Update the cache immediately so if they navigate back, they see the new data
      queryClient.setQueryData<OnboardingStateResult | undefined>(queryKey, (oldData) => {
        if (!oldData || !oldData.onboarding) return oldData;

        const updatedOnboarding: OnboardingState = { ...oldData.onboarding };
        if (typeof variables.step === 'number') {
          updatedOnboarding.lastCompletedStep = Math.max(
            updatedOnboarding.lastCompletedStep ?? 0,
            variables.step,
          );
        }

        if (variables.step === 1) {
          updatedOnboarding.step1_experience = variables.data as string | string[];
        } else if (variables.step === 2) {
          updatedOnboarding.step2_client_types = variables.data as string[];
        } else if (variables.step === 3) {
          updatedOnboarding.step3_offers = variables.data as string[];
        } else if (variables.step === 4) {
          if (
            typeof variables.data === 'object' &&
            variables.data !== null &&
            (('skipped' in variables.data && (variables.data as { skipped?: boolean }).skipped) ||
              ('onboardingSkipped' in variables.data &&
                (variables.data as { onboardingSkipped?: boolean }).onboardingSkipped))
          ) {
            updatedOnboarding.onboardingSkipped = true;
          } else {
            updatedOnboarding.step4_schedule = variables.data as {
              timezone: string;
              weekly_schedule?: Record<string, unknown>;
            };
            updatedOnboarding.lastCompletedStep = 4;
            updatedOnboarding.onboardingSkipped = false;
            updatedOnboarding.skipped = false;
          }
        } else if (variables.step === 'skip') {
          updatedOnboarding.onboardingSkipped = true;
        }

        // Keep session store profile in sync immediately
        sessionStore.getState().updateProfile({ onboarding: updatedOnboarding });

        const updatedProfile = oldData.profile
          ? {
              ...oldData.profile,
              onboarding: updatedOnboarding,
            }
          : undefined;

        return {
          ...oldData,
          onboarding: updatedOnboarding,
          ...(updatedProfile ? { profile: updatedProfile } : {}),
        };
      });

      // Still invalidate so the app fetches the real updated state (including profile, lastCompletedStep, etc)
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
